import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { postNamedQuery, postNamedCommand } from '../service'

const POLICY_QUERY_KEY = ['programPolicies', 'smart_endpoint']
const PROGRAMS_QUERY_KEY = ['programs', 'smart_endpoint']
const TAGS_QUERY_KEY_PREFIX = 'programTags'
const TAG_HISTORY_QUERY_KEY_PREFIX = 'programTagHistory'

export const smartEndpoints = {
  useExecuteQuery: () => {
    return useMutation({
      mutationFn: async (request) => {
        const { data } = await postNamedQuery('coreData', 'denali-smart-query-sandbox', request)
        return data
      }
    })
  },

  useExecuteCommand: () => {
    return useMutation({
      mutationFn: async (request) => {
        const { data } = await postNamedCommand('coreData', 'denali-smart-command-sandbox', request)
        return data
      }
    })
  },

  useInvokeSmartEndpoint: () => {
    return useMutation({
      mutationFn: async ({ code, args }) => {
        const { data } = await postNamedCommand('coreData', 'invoke-smart-endpoint', { code, args: args || {} })
        return data
      }
    })
  },

  useListPolicies: (programType = 'smart_endpoint') => {
    return useQuery({
      queryKey: ['programPolicies', programType],
      queryFn: async () => {
        const { data } = await postNamedQuery('coreData', 'list-program-policies', { programType })
        return data.policies
      }
    })
  },

  useCreatePolicy: () => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async (policy) => {
        const { data } = await postNamedCommand('coreData', 'create-program-policy', policy)
        return data
      },
      onSuccess: () => queryClient.invalidateQueries({ queryKey: POLICY_QUERY_KEY })
    })
  },

  useModifyPolicy: () => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async (policy) => {
        const { data } = await postNamedCommand('coreData', 'modify-program-policy', policy)
        return data
      },
      onSuccess: () => queryClient.invalidateQueries({ queryKey: POLICY_QUERY_KEY })
    })
  },

  useRemovePolicy: () => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async ({ programPolicyId }) => {
        const { data } = await postNamedCommand('coreData', 'remove-program-policy', { programPolicyId })
        return data
      },
      onSuccess: () => queryClient.invalidateQueries({ queryKey: POLICY_QUERY_KEY })
    })
  },

  useListPrograms: (programType = 'smart_endpoint') => {
    return useQuery({
      queryKey: PROGRAMS_QUERY_KEY,
      queryFn: async () => {
        const { data } = await postNamedQuery('coreData', 'list-programs', { programType })
        return data.programs
      }
    })
  },

  useGetProgram: (code, version) => {
    return useQuery({
      queryKey: ['program', code, version ?? 'latest'],
      queryFn: async () => {
        const params = { code }
        if (version) params.version = version
        const { data } = await postNamedQuery('coreData', 'get-program', params)
        return data
      },
      enabled: !!code
    })
  },

  useSaveProgram: () => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async (program) => {
        const { data } = await postNamedCommand('coreData', 'save-program', program)
        return data
      },
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({ queryKey: PROGRAMS_QUERY_KEY })
        queryClient.invalidateQueries({ queryKey: ['program', variables.code] })
      }
    })
  },

  useListProgramTags: (code) => {
    return useQuery({
      queryKey: [TAGS_QUERY_KEY_PREFIX, code],
      queryFn: async () => {
        const { data } = await postNamedQuery('coreData', 'list-program-tags', { code })
        return data.tags
      },
      enabled: !!code
    })
  },

  useListProgramTagHistory: (code, tagName) => {
    return useQuery({
      queryKey: [TAG_HISTORY_QUERY_KEY_PREFIX, code, tagName || 'all'],
      queryFn: async () => {
        const params = { code }
        if (tagName) params.tagName = tagName
        const { data } = await postNamedQuery('coreData', 'list-program-tag-history', params)
        return data.history
      },
      enabled: !!code
    })
  },

  useCreateProgramTag: () => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async (tag) => {
        const { data } = await postNamedCommand('coreData', 'create-program-tag', tag)
        return data
      },
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({ queryKey: [TAGS_QUERY_KEY_PREFIX, variables.code] })
        queryClient.invalidateQueries({ queryKey: [TAG_HISTORY_QUERY_KEY_PREFIX, variables.code] })
        queryClient.invalidateQueries({ queryKey: PROGRAMS_QUERY_KEY })
      }
    })
  },

  usePromoteProgramTag: () => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async (tag) => {
        const { data } = await postNamedCommand('coreData', 'promote-program-tag', tag)
        return data
      },
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({ queryKey: [TAGS_QUERY_KEY_PREFIX, variables.code] })
        queryClient.invalidateQueries({ queryKey: [TAG_HISTORY_QUERY_KEY_PREFIX, variables.code] })
        queryClient.invalidateQueries({ queryKey: PROGRAMS_QUERY_KEY })
      }
    })
  },

  useDeleteProgramTag: () => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async (tag) => {
        const { data } = await postNamedCommand('coreData', 'delete-program-tag', tag)
        return data
      },
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({ queryKey: [TAGS_QUERY_KEY_PREFIX, variables.code] })
        queryClient.invalidateQueries({ queryKey: [TAG_HISTORY_QUERY_KEY_PREFIX, variables.code] })
        queryClient.invalidateQueries({ queryKey: PROGRAMS_QUERY_KEY })
      }
    })
  }
}
